import Emitter from "services/UtilsService/Emitter";
import Cookies from "js-cookie";
import { appInsights } from "../AppInsights";
import { FORTIFIED_DATA_API_URL } from "../config";
import { setIsDownloading } from "reduxStore/api/api.actions";
import { store } from "reduxStore/store";
import { ApiCallParams } from "./ApiCallParams";
import { AUTH_API_PATH } from "Consts";
import { getApiPath, getApiVersion, downloadFile } from "./apiUtils";
import { formatError } from "../utils/errorFormater";

export default class API {
  static getAuthHeaders() {
    return {};
  }

  /**
   * @param {ApiCallParams} apiCallParams
   */
  async call(apiCallParams: ApiCallParams) {
    if (apiCallParams.responseType === "blob") {
      store.dispatch(setIsDownloading(true));
    }

    const abortController = new AbortController();
    const headers = apiCallParams.headers;
    const successMessage = apiCallParams?.successMessage;
    const failureMessage = apiCallParams?.failureMessage;

    const protectionCookie = Cookies.get("XSRF-TOKEN");

    try {
      const normalizedParams: RequestInit = {
        method: apiCallParams.method,
        headers: protectionCookie ? { ...headers, "XSRF-TOKEN": protectionCookie } : headers,
        signal: abortController.signal,
        credentials: "include",
      };

      if (apiCallParams.body) {
        normalizedParams.body = apiCallParams.body;
      }
      const path = getApiPath(apiCallParams);

      const query =
        Object.keys(apiCallParams.query).length > 0
          ? `?${Object.keys(apiCallParams.query)
              .map((key) => `${key}=${encodeURIComponent(apiCallParams.query[key])}`)
              .join("&")}`
          : "";

      const url = `${FORTIFIED_DATA_API_URL}/${getApiVersion(apiCallParams)}/${path}${query}`;

      try {
        Emitter.on("pageUnload", () => {
          abortController.abort();
        });

        Emitter.on("cancelRequest", () => {
          abortController.abort();
        });

        Emitter.off("pageUnload", () => {});

        let response = await fetch(url, normalizedParams);

        if ([200, 201, 204].includes(response.status) && successMessage) {
          Emitter.emit("displaySuccess", successMessage);
        }

        // 401 unauthorized
        if (response.status === 401 && path !== AUTH_API_PATH) {
          // retry one more time
          await new Promise((f) => setTimeout(f, 2000));
          response = await fetch(url, normalizedParams);

          if (response.status === 401) {
            appInsights?.trackTrace({
              message: "pre logout response.status = 401",
              properties: {
                origin: "Api.js",
                request: url,
              },
            });

            window.location.href = `//${window.location.hostname}${
              window.location.port ? `:${window.location.port}` : ""
            }/logout`;

            return;
          }
        }

        // 403 Access not authorized
        if (response.status === 403) {
          Emitter.emit("displayApiErrors", [{ path: "general", message: "Access not authorized" }]);

          appInsights?.trackTrace({
            message: "unauthorized access registered",
            properties: {
              origin: "Api.js",
              request: url,
            },
          });

          return {
            errors: [{ path: "general", message: "Not authorized Action" }],
          };
        }

        // response type json
        if (!apiCallParams.responseType || apiCallParams.responseType === "json") {
          if (response.status === 404) {
            return { data: null, status: 404 };
          }

          const text = await response.text();
          const json = text ? JSON.parse(text) : {};

          if (response.status >= 400 && !json.errors && !path.includes("invite")) {
            json.errors = [{ path: "general", message: "Unknown Server Error." }];

            appInsights?.trackTrace({
              message: "unknown api call error",
              properties: {
                origin: "Api.js",
                request: url,
                requestBody: normalizedParams,
              },
            });

            if (json.Errors) {
              Emitter.emit("displayApiErrors", json.Errors);
            } else {
              Emitter.emit(
                "displayFailure",
                formatError({ errorId: json?.errorId, text: "Unknown error has occurred." })
              );
            }
          } else if (json.errors) {
            const errorMessage = json?.errors[0]?.message || failureMessage;

            Emitter.emit("displayApiErrors", [
              {
                message: formatError({ errorId: json?.errorId, text: errorMessage }),
                title: "Error",
              },
            ]);
            if (json.errors.length > 0) {
              Emitter.emit("displayApiErrors", json.errors);
            }
          }

          if (typeof json === "object" && !Array.isArray(json) && json !== null) {
            json.isAborted = false;
            json.status = response.status;
          }
          return json;
        }

        if (apiCallParams.responseType === "blob") {
          const blob = await response.blob();
          if (response.status >= 400) {
            Emitter.emit("displayFailure", "Download error occurred.");
          } else {
            downloadFile(response, blob);
          }
          return null;
        } else if (apiCallParams.responseType === "file") {
          const blob = await response.blob();
          return blob;
        } else {
          throw new Error(`unsupported response type ${apiCallParams.responseType}`);
        }
      } catch (error: any) {
        console.log("*** error -- ", error);
        // need to check the flow here
        if (error.name === "AbortError") return { data: [], isAborted: true };

        const json = {
          errors: [
            {
              path: "general",
              message:
                "An internal error occurred while calling the server. Please try to reload the page.",
            },
          ],
        };

        Emitter.emit("displayApiErrors", json.errors);

        appInsights?.trackTrace({
          message: "invalid api call endpoint error",
          properties: {
            origin: "Api.js",
            request: url,
            requestBody: normalizedParams,
            error,
          },
        });
        return json;
      }
    } catch (error: any) {
      console.log(
        "There has been a problem with the fetch operation: ",
        apiCallParams,
        error.message
      );
      return null;
    } finally {
      if (apiCallParams.responseType === "blob") {
        store.dispatch(setIsDownloading(false));
      }
    }
  }
}
